import { Position, PositionAngle } from '../constants';
import { Shelf } from './Shelf';
import { Color } from './Color';
import { Pane } from '../pieces/Pane';
import { CustomPiece } from '../pieces/CustomPiece';
import { Door } from '../pieces/Door';
import { Rack } from './Rack';
import cloneDeep from 'lodash/cloneDeep';

export class ShelfSelection {
  get panes(): Position[] {
    return this._panes;
  }

  set panes(value: Position[]) {
    this._panes = value;
  }
  /**
   * Selection for the panes of the shelf
   */
  private _doors: PositionAngle[];
  private _panes: Position[] = [];
  private _variableRack = false;
  private _allRack: boolean;
  private _rackLevelUnSelected: number[] = [];

  constructor(shelves: Shelf[] = []) {
    this._doors = [];
    this._allRack = true;
    this._variableRack = ShelfSelection.isVariableRack(shelves);
  }

  static isVariableRack(shelves: Shelf[]) {
    let racksNumber = null;
    if (shelves.length > 0) {
      for (const shelf of shelves) {
        if (racksNumber && shelf.racks.length !== racksNumber) {
          return true;
        } else {
          racksNumber = shelf.racks.length;
        }
      }
    }
    return false;
  }

  clone(): ShelfSelection {
    return cloneDeep(this);
  }

  get variableRack(): boolean {
    return this._variableRack;
  }

  get doors(): PositionAngle[] {
    return this._doors;
  }

  set doors(positions: PositionAngle[]) {
    for (const position of positions) this.selectDoor(position);
  }

  get allRack(): boolean {
    return this._allRack;
  }

  get rackLevelUnSelected(): number[] {
    return this._rackLevelUnSelected;
  }

  set rackLevelUnSelected(value: number[]) {
    this._rackLevelUnSelected = value;
  }

  selectDoor(position: PositionAngle) {
    if (!this._doors.includes(position)) this._doors.push(position);
  }

  isDoorSelected(position: PositionAngle): boolean {
    return this._doors.includes(position);
  }

  unselectDoor(position: PositionAngle) {
    this._doors = this._doors.filter(item => item !== position);
  }

  getSelectedDoors(shelves: Shelf[]): CustomPiece<Door>[] {
    const doorsSelected = [];
    for (const shelf of shelves) {
      for (const position of this._doors) {
        const cpDoor = shelf.getDoor(position);
        if (cpDoor) doorsSelected.push(cpDoor);
      }
    }

    return doorsSelected;
  }

  getSelectedDoorColor(
    shelves: Shelf[],
    position: PositionAngle,
  ): Color | null {
    let color: Color | null = null;
    for (const shelf of shelves) {
      if (this._doors.includes(position)) {
        const door = shelf.getDoor(position);
        if (door) {
          if (color && door.color !== color) {
            return null;
          } else {
          }
          color = door.color;
        }
      }
    }

    return color;
  }

  getDoorColorAtPosition(
    shelves: Shelf[],
    position: PositionAngle,
  ): Color | null {
    let color: Color | null = null;
    for (const shelf of shelves) {
      const door = shelf.getDoor(position);
      if (door) {
        if (color && door.color.reference !== color.reference) {
          return null;
        }
        color = door.color;
      }
    }

    return color;
  }

  getSelectedRack(racks: Rack[]) {
    /**
     * Filter out the unselected racks
     */
    let selectedRacks = racks;
    if (!this._allRack) {
      selectedRacks = racks.filter(
        rack => !this._rackLevelUnSelected.includes(rack.level),
      );
    }
    return selectedRacks;
  }

  getSelectedPanes(shelves: Shelf[]): CustomPiece<Pane>[] {
    const panesSelected = [];
    for (const shelf of shelves) {
      for (const position of this.panes) {
        for (const rack of this.getSelectedRack(shelf.racks)) {
          const cp = rack.getPane(position);
          if (cp) panesSelected.push(cp);
        }
      }
    }
    return panesSelected;
  }

  getSelectedPaneColor(shelves: Shelf[], position: Position): Color | null {
    let color: Color | null = null;
    for (const shelf of shelves) {
      for (const rack of this.getSelectedRack(shelf.racks)) {
        const customPiecePane = rack.getPane(position);
        if (customPiecePane) {
          if (color && customPiecePane.color.reference !== color.reference) {
            return null;
          } else {
            color = customPiecePane.color;
          }
        }
      }
    }
    if (color) return color;
    return null;
  }

  get numberElementSelected() {
    return this._doors.length + this._panes.length;
  }

  getCommonColor(shelves: Shelf[]): Color | undefined {
    /**
     * Retrieve the common color across all selection
     */

    let commonColor: Color | undefined = undefined;
    if (shelves.length === 0) return undefined;

    if (this.doors.length > 0) {
      if (!commonColor) {
        commonColor = shelves[0].getDoor(this.doors[0])?.color;
      }
      for (const shelf of shelves) {
        for (const position of this.doors) {
          if (
            shelf.getDoor(position)?.color.id &&
            shelf.getDoor(position)?.color.id !== commonColor?.id
          ) {
            return undefined;
          }
        }
      }
    }

    if (this.panes.length > 0) {
      for (const shelf of shelves) {
        for (const rack of shelf.racks) {
          for (const position of this.panes) {
            if (!commonColor) {
              commonColor = rack.getPane(position)?.color;
            } else if (
              rack.getPane(position)?.color.id &&
              rack.getPane(position)?.color.id !== commonColor?.id
            ) {
              return undefined;
            }
          }
        }
      }
    }

    return commonColor;
  }

  selectPane(position: Position) {
    if (!this._panes.includes(position)) this._panes.push(position);
  }

  isPaneSelected(position: Position) {
    return !!this._panes.find(item => item === position);
  }

  unselectPane(position: Position) {
    this._panes = this._panes.filter(item => item !== position);
  }

  toggleDoor(position: PositionAngle, multi = false) {
    if (!this.doors.includes(position)) {
      if (!multi) {
        this.unselectAll();
      }
      this.doors.push(position);
    } else {
      if (!multi) {
        this.unselectAll();
        this.doors.push(position);
      } else {
        // Never have nothing selected
        if (this.numberElementSelected > 1) this.unselectDoor(position);
      }
    }
  }

  togglePane(position: Position, multi = false) {
    if (!this._panes.includes(position)) {
      if (!multi) {
        this.unselectAll();
      }
      this._panes.push(position);
    } else {
      if (!multi) {
        this.unselectAll();
        this._panes.push(position);
      } else {
        // Never have nothing selected
        if (this.numberElementSelected > 1) this.unselectPane(position);
      }
    }
  }

  unselectAll() {
    this._doors = [];
    this._panes = [];
  }

  static checkDoorPresentAtPosition(
    position: PositionAngle,
    shelves: Shelf[],
  ): boolean {
    for (const shelf of shelves) {
      if (shelf.getDoor(position)) return true;
    }
    return false;
  }

  static checkPanePresentAtPosition(
    position: Position,
    shelves: Shelf[],
  ): boolean {
    for (const shelf of shelves) {
      for (const rack of shelf.racks) {
        if (rack.getPane(position)) return true;
      }
    }
    return false;
  }

  update(shelves: Shelf[]) {
    // Doors
    if (this._doors.length > 0) {
      for (const position of this._doors) {
        if (!ShelfSelection.checkDoorPresentAtPosition(position, shelves)) {
          this.unselectDoor(position);
        }
      }
    }

    // Panes
    if (this._panes.length > 0) {
      for (const position of this._panes) {
        if (!ShelfSelection.checkPanePresentAtPosition(position, shelves)) {
          this.unselectPane(position);
        }
      }
    }

    this._variableRack = ShelfSelection.isVariableRack(shelves);

    for (const shelf of shelves) {
      for (const rack of shelf.racks) {
        if (this._rackLevelUnSelected.includes(rack.level)) {
          this._rackLevelUnSelected = this._rackLevelUnSelected.filter(
            item => item !== rack.level,
          );
        }
      }
    }
  }

  all() {
    this.unselectAll();
    for (const position of Object.values(PositionAngle)) {
      this.selectDoor(position);
    }

    this.selectPane(Position.Left);
    this.selectPane(Position.Right);
    this.selectPane(Position.Back);

    return this;
  }

  allDoors() {
    this.unselectAll();
    for (const position of Object.values(PositionAngle)) {
      this.selectDoor(position);
    }
    return this;
  }

  allSides() {
    this.unselectAll();
    this.selectPane(Position.Left);
    this.selectPane(Position.Right);
    this.selectPane(Position.Back);

    return this;
  }
}
